import React, { useCallback, useMemo, useState } from "react";
import { groupBy } from "lodash-es";
import { Add, Bin, Edit } from "react-ikonate";
import { ReactComponent as SubcategoryIcon } from "shared/assets/SubCategory Icon.svg";
import createNotification from "shared/@utils/createNotification";
import { ascendingBy } from "shared/@utils/sort";
import Button, { ButtonSize } from "shared/Button";
import ControlsHeader from "shared/ControlsHeader";
import { Modal } from "shared/Modal";
import styles from "./AppCategories.module.css";
import CategoryBanner from "../components/CategoryBanner";
import { CategoryEdit } from "../components/CategoryEdit/CategoryEdit";
import CategoryListItem from "../components/CategoryListItem";
import TagMonster from "../components/TagMonster";
import { useAppCategoriesData } from "./useAppCategoriesData";
import {
  applyCategoryMove,
  getCategoryDropPosition,
  getCategoryMove,
} from "../categoryDrag";

/**
 * Structure component. Provides generic category editing functionality for app content (Retail & Digital apps).
 */
export function AppCategories({ appId, pageTitle, showAppPreview = true }) {
  const data = useAppCategoriesData(appId);
  const [addIsOpen, setAddIsOpen] = useState(false);
  const [previewCategory, setPreviewCategory] = useState(null);
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const onPreviewCategoryChange = useCallback((category) => {
    setPreviewCategory(
      category ? { ...category, id: category.id ?? -1 } : null
    );
  }, []);
  const previewCategories = useMemo(() => {
    if (!previewCategory) return data.categories;

    const hasExistingCategory = data.categories.some(
      (category) => category.id === previewCategory.id
    );

    return hasExistingCategory
      ? data.categories.map((category) =>
          category.id === previewCategory.id ? previewCategory : category
        )
      : [...data.categories, previewCategory];
  }, [data.categories, previewCategory]);

  const canSaveCategory = useCallback(
    (category) => {
      if (!category.isPublished) return true;

      const rowNumber = Number(category.rowNumber || 1);
      const siblings = data.categories.filter(
        (existing) =>
          existing.isPublished &&
          (existing.parentCategoryID ?? null) ===
            (category.parentCategoryID ?? null) &&
          Number(existing.rowNumber || 1) === rowNumber &&
          (category.id == null || existing.id !== category.id)
      );

      if (siblings.length < 4) return true;

      createNotification({
        type: "warning",
        title: "Row Full",
        message: `There are already 4 categories in Row ${rowNumber}. Choose another row or save as draft.`,
        autoDismiss: true,
      });
      return false;
    },
    [data.categories]
  );

  const getSubcategories = useCallback(
    (categoryId) => {
      return data.categories.filter(
        (category) => category.parentCategoryID === categoryId
      );
    },
    [data.categories]
  );

  const onSubcategoryAdd = useCallback(
    (category, parentId) => {
      const subcategory = {
        ...category,
        parentCategoryID: parentId,
      };
      if (!canSaveCategory(subcategory)) return false;
      return data.addCategoryMutation.mutateAsync(subcategory);
    },
    [canSaveCategory, data.addCategoryMutation]
  );

  const onCategoryUpdate = useCallback(
    (category) => {
      if (!canSaveCategory(category)) return false;
      data.setCategories(
        data.categories.map((c) => {
          if (c.id === category.id) {
            return category;
          }

          return c;
        })
      );
      return true;
    },
    [canSaveCategory, data]
  );

  const onCategoryDelete = useCallback(
    (category) => {
      data.setCategories(data.categories.filter((c) => c.id !== category.id));

      createNotification({
        type: "info",
        title: "Category Deleted",
        message: "Click Cancel or refresh the page to undo.",
        autoDismiss: true,
      });
    },
    [data.categories]
  );

  function getMove(sourceId, targetId, position, rowNumber, parentId) {
    return getCategoryMove(
      data.categories,
      sourceId,
      targetId,
      position,
      rowNumber,
      parentId
    );
  }

  function onReorder(sourceId, targetId, position, rowNumber, parentId) {
    const move = getMove(sourceId, targetId, position, rowNumber, parentId);
    if (!move) return;
    const moved = {
      ...move.source,
      rowNumber: move.rowNumber,
      parentCategoryID: move.parentCategoryID,
    };
    if (!canSaveCategory(moved)) return;
    data.setCategories(applyCategoryMove(data.categories, move));
  }

  if (data.isLoading) {
    return "Loading...";
  }

  if (data.isError) {
    console.error(data.error);
    return data.error.message;
  }

  const isSaving = data.saveMutation.isLoading;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <ControlsHeader title={pageTitle}>
          {isSaving && (
            <span className={styles.savingBadge} role="status">
              Saving changes...
            </span>
          )}
          <Button
            icon={<Add />}
            disabled={addIsOpen}
            onClick={() => {
              setAddIsOpen(true);
            }}
          >
            New Category
          </Button>

          <Button
            disabled={!data.isChanged || isSaving}
            onClick={() => {
              data.saveMutation.mutate();
            }}
            purpose="save"
          >
            {isSaving ? (
              <span className={styles.saveSpinner} aria-hidden="true" />
            ) : (
              "Save"
            )}
          </Button>

          <Button
            disabled={!data.isChanged || isSaving}
            onClick={() => {
              data.reset();
            }}
          >
            Cancel
          </Button>
        </ControlsHeader>
      </div>

      {showAppPreview &&
        data.categories.some((category) => category.isPublished) && (
          <div className={styles.tagPreview}>
            <h4 className={styles.tagPreviewTitle}>App Preview</h4>
            <div className={styles.tagPreviewFrame}>
              <TagMonster categories={previewCategories} />
            </div>
          </div>
        )}

      {addIsOpen && (
        <div className={styles.categoryAdd}>
          <CategoryEdit
            showPreview={true}
            onCategoryChange={onPreviewCategoryChange}
            onSubmit={async (category) => {
              if (!canSaveCategory(category)) return;
              await data.addCategoryMutation.mutateAsync(category);
              setPreviewCategory(null);
              setAddIsOpen(false);
            }}
            onCancel={() => {
              setPreviewCategory(null);
              setAddIsOpen(false);
            }}
          />
        </div>
      )}

      <CategoryList
        categories={data.categories}
        getMove={getMove}
        draggedCategoryId={draggedCategoryId}
        setDraggedCategoryId={setDraggedCategoryId}
        dropTarget={dropTarget}
        setDropTarget={setDropTarget}
        onReorder={onReorder}
        getSubcategories={getSubcategories}
        onSubcategoryAdd={onSubcategoryAdd}
        onCategoryUpdate={onCategoryUpdate}
        onCategoryDelete={onCategoryDelete}
        onPreviewCategoryChange={onPreviewCategoryChange}
      />
    </div>
  );
}

function CategoryList({
  parentCategoryID = null,
  categories,
  getMove,
  draggedCategoryId,
  setDraggedCategoryId,
  dropTarget,
  setDropTarget,
  onReorder,
  getSubcategories,
  onSubcategoryAdd,
  onCategoryUpdate,
  onCategoryDelete,
  onPreviewCategoryChange,
}) {
  const [editCategory, setEditCategory] = useState(null);

  const [isAddingSubcategory, setIsAddingSubcategory] = useState(false);
  const [subcategoryParent, setSubcategoryParent] = useState(null);

  const topLevel = categories.filter(
    (c) => c.parentCategoryID == parentCategoryID
  );
  const published = topLevel.filter((c) => c.isPublished);
  const drafts = topLevel.filter((c) => !c.isPublished);

  const byRow = groupBy(published, (x) => x.rowNumber || 1);
  const groups = Array.from({ length: 4 }, (_, index) => ({
    name: `Row ${index + 1}`,
    rowNumber: index + 1,
    categories: (byRow[index + 1] || []).sort(ascendingBy("stepNumber")),
  }));
  const inGroupedOrder = groups.reduce(
    (arr, group) => [...arr, ...group.categories],
    []
  );

  function clearDrag() {
    setDraggedCategoryId(null);
    setDropTarget(null);
  }

  function handleDragOver(event, categoryId, rowNumber) {
    event.stopPropagation();
    if (draggedCategoryId == null || draggedCategoryId === categoryId) return;
    const position = getCategoryDropPosition(
      event,
      !!getMove(
        draggedCategoryId,
        categoryId,
        "inside",
        rowNumber,
        parentCategoryID
      )
    );
    if (
      !getMove(
        draggedCategoryId,
        categoryId,
        position,
        rowNumber,
        parentCategoryID
      )
    ) {
      setDropTarget(null);
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropTarget({
      categoryId,
      rowNumber,
      parentCategoryID,
      position,
    });
  }

  function handleDrop(event, categoryId, rowNumber) {
    event.preventDefault();
    event.stopPropagation();
    if (draggedCategoryId != null) {
      const position =
        dropTarget?.categoryId === categoryId &&
        dropTarget.rowNumber === rowNumber &&
        dropTarget.parentCategoryID === parentCategoryID
          ? dropTarget.position
          : getCategoryDropPosition(event, false);
      onReorder(
        draggedCategoryId,
        categoryId,
        position,
        rowNumber,
        parentCategoryID
      );
    }
    clearDrag();
  }

  function handleDragLeave(event, categoryId, rowNumber) {
    event.stopPropagation();
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    )
      return;
    setDropTarget((current) =>
      current?.categoryId === categoryId &&
      current.rowNumber === rowNumber &&
      current.parentCategoryID === parentCategoryID
        ? null
        : current
    );
  }

  return (
    <>
      <ul className={styles.categoryList}>
        {groups.map((group) => {
          return (
            <li key={group.name} className={styles.categoryGroup}>
              <h4 className={styles.groupHeader}>{group.name}</h4>
              <ul>
                {group.categories.length === 0 && (
                  <li
                    className={styles.emptyRow}
                    data-row-number={group.rowNumber}
                    data-drop-position={
                      dropTarget?.categoryId == null &&
                      dropTarget?.rowNumber === group.rowNumber &&
                      dropTarget?.parentCategoryID === parentCategoryID
                        ? dropTarget.position
                        : undefined
                    }
                    aria-label={`${group.name}, empty`}
                    onDragOver={(event) =>
                      handleDragOver(event, null, group.rowNumber)
                    }
                    onDrop={(event) => handleDrop(event, null, group.rowNumber)}
                    onDragLeave={(event) =>
                      handleDragLeave(event, null, group.rowNumber)
                    }
                  />
                )}
                {group.categories.map((category) => {
                  const subcategories = getSubcategories(category.id);

                  return (
                    <CategoryListItem
                      key={category.id}
                      compactLayout
                      isDragging={draggedCategoryId === category.id}
                      dropPosition={
                        draggedCategoryId != null &&
                        dropTarget?.categoryId === category.id
                          ? dropTarget.position
                          : null
                      }
                      dragProps={{
                        draggable: true,
                        "data-category-id": category.id,
                        onDragStart: (event) => {
                          event.stopPropagation();
                          if (event.target.closest("button, input, form")) {
                            event.preventDefault();
                            return;
                          }
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData(
                            "text/plain",
                            String(category.id)
                          );
                          setDraggedCategoryId(category.id);
                        },
                        onDragEnd: (event) => {
                          event.stopPropagation();
                          clearDrag();
                        },
                        onDragOver: (event) =>
                          handleDragOver(event, category.id, group.rowNumber),
                        onDrop: (event) =>
                          handleDrop(event, category.id, group.rowNumber),
                        onDragLeave: (event) =>
                          handleDragLeave(event, category.id, group.rowNumber),
                      }}
                      onReorderKeyDown={(event) => {
                        if (!["ArrowUp", "ArrowDown"].includes(event.key))
                          return;
                        event.preventDefault();
                        const direction = event.key === "ArrowUp" ? -1 : 1;
                        const neighbor =
                          inGroupedOrder[
                            inGroupedOrder.indexOf(category) + direction
                          ];
                        if (neighbor) {
                          onReorder(
                            category.id,
                            neighbor.id,
                            direction < 0 ? "above" : "below",
                            Number(neighbor.rowNumber || 1)
                          );
                        }
                      }}
                      content={
                        <div className={styles.categoryBanner}>
                          <CategoryBanner
                            title={category.title}
                            fillColor={
                              category.colorFill
                                ? category.backgroundColor
                                : null
                            }
                            textColor={category.textColor}
                          />
                        </div>
                      }
                      controls={
                        <>
                          <Button
                            icon={<Edit />}
                            size={ButtonSize.small}
                            onClick={() => {
                              setEditCategory(category);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            icon={<SubcategoryIcon aria-hidden="true" />}
                            size={ButtonSize.small}
                            onClick={() => {
                              setIsAddingSubcategory(true);
                              setSubcategoryParent(category.id);
                            }}
                          >
                            Add Subcategory
                          </Button>
                          <Button
                            icon={<Bin />}
                            size={ButtonSize.small}
                            disabled={subcategories.length > 0}
                            onClick={() => {
                              onCategoryDelete(category);
                            }}
                          >
                            Delete Category
                          </Button>
                        </>
                      }
                      subcategoriesCount={subcategories.length}
                      renderSubcategories={() => {
                        return (
                          <CategoryList
                            parentCategoryID={category.id}
                            categories={subcategories}
                            getMove={getMove}
                            draggedCategoryId={draggedCategoryId}
                            setDraggedCategoryId={setDraggedCategoryId}
                            dropTarget={dropTarget}
                            setDropTarget={setDropTarget}
                            onReorder={onReorder}
                            getSubcategories={getSubcategories}
                            onSubcategoryAdd={onSubcategoryAdd}
                            onCategoryUpdate={onCategoryUpdate}
                            onCategoryDelete={onCategoryDelete}
                            onPreviewCategoryChange={onPreviewCategoryChange}
                          />
                        );
                      }}
                    />
                  );
                })}
              </ul>
            </li>
          );
        })}

        {drafts.length > 0 && (
          <li className={styles.categoryGroup}>
            <h4 className={styles.groupHeader}>Drafts</h4>
            <ul>
              {drafts.map((subcategory) => (
                <CategoryListItem
                  key={subcategory.id}
                  compactLayout
                  content={
                    <div className={styles.categoryBanner}>
                      <CategoryBanner
                        title={subcategory.title}
                        fillColor={
                          subcategory.colorFill
                            ? subcategory.backgroundColor
                            : null
                        }
                        textColor={subcategory.textColor}
                      />
                    </div>
                  }
                  controls={
                    <>
                      <Button
                        icon={<Edit />}
                        size={ButtonSize.small}
                        onClick={() => {
                          setEditCategory(subcategory);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        icon={<Bin />}
                        size={ButtonSize.small}
                        onClick={() => {
                          onCategoryDelete(subcategory);
                        }}
                      >
                        Delete Category
                      </Button>
                    </>
                  }
                />
              ))}
            </ul>
          </li>
        )}
      </ul>

      <Modal
        title="Add Subcategory"
        isOpen={isAddingSubcategory}
        onClose={() => {
          setIsAddingSubcategory(false);
        }}
      >
        {isAddingSubcategory && (
          <CategoryEdit
            showPreview={true}
            onCategoryChange={(category) =>
              onPreviewCategoryChange({
                ...category,
                parentCategoryID: subcategoryParent,
              })
            }
            onSubmit={async (category) => {
              const result = await onSubcategoryAdd(
                category,
                subcategoryParent
              );
              if (result === false) return;
              onPreviewCategoryChange(null);
              setIsAddingSubcategory(false);
            }}
            onCancel={() => {
              onPreviewCategoryChange(null);
              setIsAddingSubcategory(false);
            }}
          />
        )}
      </Modal>

      <Modal
        title="Edit Category"
        isOpen={Boolean(editCategory)}
        onClose={() => {
          setEditCategory(null);
        }}
      >
        {editCategory && (
          <CategoryEdit
            category={editCategory}
            showPreview={true}
            onCategoryChange={onPreviewCategoryChange}
            onSubmit={(updated) => {
              if (!onCategoryUpdate(updated)) return;
              onPreviewCategoryChange(null);
              setEditCategory(null);
            }}
            onCancel={() => {
              onPreviewCategoryChange(null);
              setEditCategory(null);
            }}
          />
        )}
      </Modal>
    </>
  );
}
